import useConfig from "@/features/config/hooks/useConfig.tsx";
import "./styles/Selection.scss";
import type { ChangeEvent, CSSProperties, MouseEvent } from "react";
import { useEffect, useRef, useState } from "react";
import {
  CATEGORY_QUESTION_ID,
  NO_PREFERENCE,
  RANGE_SEPARATOR,
  selectionStore
} from "@/features/widgets/components/Selection/utils/selectionStore.tsx";
import Dialog from "@/features/dialog/components/Dialog/Dialog.tsx";
import { useTranslation } from "react-i18next";
import useAuthorizedUrl from "@/features/widgets/hooks/useAuthorizedUrl.tsx";
import DataHelper from "@/utils/dataHelper.tsx";
import Loader from "@/components/ui/Loader/Loader.tsx";
import Messages from "@/components/ui/Messages/Messages.tsx";
import Button from "@/components/ui/Buttons/Button/Button.tsx";

import IconRadioChecked from "@/assets/icons/radio-checked.svg?react";
import IconChevronLeft from "@/assets/icons/chevron-left.svg?react";
import IconReset from "@/assets/icons/reset.svg?react";
import IconSkip from "@/assets/icons/skip.svg?react";
import SelectionProduct from "@/features/widgets/components/Selection/SelectionProduct.tsx";
import uiHelper from "@/utils/uiHelper.tsx";
import Title from "@/components/ui/Title/Title.tsx";
import ProgressBar from "@/components/ui/ProgressBar/ProgressBar.tsx";
import useTrackEvent from "@/features/widgets/hooks/useTrackEvent.tsx";

const CLASS_PREFIX = `ws-widgets-selection`;

// Must match the opacity transition duration in Selection.scss.
const FADE_DURATION_MS = 200;

const Selection = () => {
  const { t } = useTranslation();
  const config = useConfig();

  const [visible, setVisible] = useState(false);
  const [categoryId, setCategoryId] = useState<number | null>(null);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [generation, setGeneration] = useState(0);
  const [questions, setQuestions] = useState<SelectionQuestion[] | null>(null);
  const [result, setResult] = useState<SelectionResult | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [questionLimit, setQuestionLimit] = useState<number | null>(null);
  const [rangeDrafts, setRangeDrafts] = useState<Record<number, SelectionRange>>({});
  const [fadedOut, setFadedOut] = useState(false);
  const fadeTimeout = useRef<ReturnType<typeof setTimeout>>();
  const fading = useRef(false);

  const cssBackendHost = config.backendHost.slice(0, -2);
  const cssPath = `${ cssBackendHost }/sites/default/files/domains/${ config.domainId }/styling.css`;

  useEffect(() => {
    const unsubscribe = selectionStore.subscribe(state => {
      setCategoryId(state.categoryId);
      setVisible(state.active);
      setCurrentQuestionIndex(state.currentQuestionIndex);
      setSessionId(state.sessionId);
      setGeneration(state.generation);
    });
    return unsubscribe;
  }, []);

  useEffect(() => () => clearTimeout(fadeTimeout.current), []);

  const cancelFade = () => {
    clearTimeout(fadeTimeout.current);
    fading.current = false;
    setFadedOut(false);
  }

  const fadeOutDuring = (action: () => Promise<void> | void) => {
    if (fading.current) {
      return;
    }

    fading.current = true;
    setFadedOut(true);
    fadeTimeout.current = setTimeout(async () => {
      try {
        await action();
      } finally {
        fading.current = false;
        setFadedOut(false);
      }
    }, FADE_DURATION_MS);
  }

  const goBack = () => {
    cancelFade();
    selectionStore.previousQuestion();
  }

  const trackEvent = useTrackEvent();

  const url = useAuthorizedUrl(`/api/v1/selection/questions`);

  if (categoryId) {
    url.searchParams.set(`category_id`, categoryId.toString());
  }
  if (sessionId) {
    url.searchParams.set(`session_id`, sessionId);
  }

  useEffect(() => {
    setQuestions(null);
    setResult(null);
    setQuestionLimit(null);
    setRangeDrafts({});

    if (!visible) {
      return;
    }

    setError(false);
    setLoading(true);

    DataHelper.get(url)
      .then((result: SelectionQuestionsResponse) => {
          if (result.session.session_id) {
            selectionStore.setSessionId(result.session.session_id);
          }
          if (result.session.expires_in !== null) {
            selectionStore.setSessionExpiresIn(result.session.expires_in);
          }
          if (result.session.category_id) {
            selectionStore.setCategoryId(result.session.category_id);
          }
          if (result.session.questions) {
            setQuestions(result.session.questions);
          }
          setQuestionLimit(result.session.question_limit);
        }
      )
      .catch(() => {
        setError(true);
      })
      .finally(() => {
        setLoading(false)
      });

  }, [visible, generation]);

  useEffect(() => {
    if (visible) {
      trackEvent(
        'open',
        'selection_dialog',
        categoryId?.toString() || null,
      );
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
      if (!visible) {
        return;
      }

      const checkExpires = setInterval(() => {
        if (selectionStore.isSessionExpired()) {
          console.warn('Session expired. Restarting.')
          selectionStore.restart();
        }
      }, 60 * 1000);

      return () => clearInterval(checkExpires);
    },
    [visible]);

  const postAnswers = async () => {
    const body = {
      session_id: selectionStore.getSessionId(),
      answers: selectionStore.getAnswersUpToCurrentQuestion(questions ?? []),
    };

    selectionStore.addPostedQuestionIndex(currentQuestionIndex);

    setError(false);
    setLoading(true);
    const result = await DataHelper.post(url, JSON.stringify(body));
    if (!result) {
      setError(true);
    }
    if (result?.session.session_id) {
      selectionStore.setSessionId(result.session.session_id);
    }
    if (result && result.session.expires_in !== null) {
      selectionStore.setSessionExpiresIn(result.session.expires_in);
    }
    if (result?.session.category_id) {
      selectionStore.setCategoryId(result?.session.category_id);
    }
    if (result?.session.questions) {
      setQuestions(result.session.questions);
      selectionStore.nextQuestion();
    }
    if (result?.session.result) {
      setResult(result.session.result);
    }
    if (result) {
      setQuestionLimit(result.session.question_limit);
    }
    setLoading(false);
  }

  const selectAnswer = (question: SelectionQuestion, option: SelectionOption) => fadeOutDuring(async () => {
    selectionStore.addAnswer(question.id, option.id);

    if (question.id === CATEGORY_QUESTION_ID) {
      selectionStore.setCategoryId(parseInt(option.id));
    }

    // Post if we're out of locally known questions, or if we're back at
    // a question that previously triggered a post.
    const shouldPostAnswers = !questions
      || currentQuestionIndex >= questions.length - 1
      || selectionStore.wasPosted(currentQuestionIndex);
    if (shouldPostAnswers) {
      await postAnswers();
      return;
    }

    selectionStore.nextQuestion();
  });

  const onRestart = () => {
    cancelFade();
    setResult(null);
    setRangeDrafts({});
    selectionStore.setCurrentQuestionIndex(0);
    trackEvent(
      'click',
      'selection_dialog_restart',
      null,
      [],
    )
  }

  const currentQuestion = questions?.[currentQuestionIndex];
  const noPreferenceOption = currentQuestion?.options.find(option => option.id === NO_PREFERENCE);
  const answerOptions = currentQuestion?.options.filter(option => option !== noPreferenceOption) ?? [];
  const currentRange = currentQuestion?.type === `numeric_range`
    ? rangeDrafts[currentQuestion.id] ?? getInitialRange(currentQuestion)
    : null;
  const showResult = !currentQuestion && result;
  const categoryQuestionCount = questions?.[0]?.id === CATEGORY_QUESTION_ID ? 1 : 0;
  const questionNumber = currentQuestionIndex - categoryQuestionCount + 1;
  const progress = questionLimit ? getProgress(questionNumber, questionLimit) : null;

  const fadeClasses = [
    `${ CLASS_PREFIX }-question__fade`,
    fadedOut ? `${ CLASS_PREFIX }-question__fade--hidden` : ``,
  ];

  return (
    <>
      <link rel="stylesheet" type="text/css" href={ cssPath }/>

      <Dialog
        data-ws-category-id={ categoryId }
        className={ `${ CLASS_PREFIX }-dialog` }
        title={ selectionStore.getDialogTitle() || t(`selection.title`) }
        open={ visible }
        fullscreen={ false }
        animateSize={ true }
        onBrowserBack={ () => {
          if (currentQuestionIndex === 0) {
            return false;
          }
          goBack();
          return true;
        } }
        onClose={ () => {
          trackEvent(
            result ? 'close' : 'close_abandon',
            'selection_dialog',
            categoryId?.toString() || null,
          );
          setVisible(false);
        } }
      >
        <>
          {
            error ?
              <Messages type={ `error` } messages={ [t(`selection.not_available`)] }/>
              :
              null
          }
          {
            !questions?.length && !loading ?
              <p>{ t(`selection.no_questions`) }</p>
              :
              null
          }
          {
            currentQuestion && currentQuestion.id !== CATEGORY_QUESTION_ID && progress !== null &&
            <ProgressBar value={ progress } className={ `${ CLASS_PREFIX }-question__progress` }/>
          }

          <div className={ `${ CLASS_PREFIX }-questions ${ loading ? `${ CLASS_PREFIX }-questions--loading` : `` }` }>
            {
              showResult && <SelectionResult products={ result.products }/>
            }
            {
              loading ?
                <Loader/>
                :
                null
            }
            <div className={ fadeClasses.join(` `) }>
              {
                currentQuestion &&
                <div key={ currentQuestion.id } className={ `${ CLASS_PREFIX }-question` }>
                  <Title level={ 2 } className={ `${ CLASS_PREFIX }-question__title` }>
                    { currentQuestion.label }
                  </Title>
                  { currentQuestion.description &&
                    <div className={ `${ CLASS_PREFIX }-question__description` }>{ currentQuestion.description }</div>
                  }
                  {
                    currentRange ?
                      <SelectionRangeQuestion
                        question={ currentQuestion }
                        value={ currentRange }
                        onChange={ range => setRangeDrafts(drafts => ({ ...drafts, [currentQuestion.id]: range })) }
                        disabled={ loading }
                      />
                      :
                      <div>
                        {
                          answerOptions.map(option =>
                            <button
                              className={ `${ CLASS_PREFIX }-question__option` }
                              onClick={ () => selectAnswer(currentQuestion, option) }
                              key={ option.id }
                              disabled={ loading }
                              data-ws-option-id={ option.id }
                            >
                                <span className={ `${ CLASS_PREFIX }-question__option-icon` }>
                                  <IconRadioChecked/>
                                </span>
                              <span className={ `${ CLASS_PREFIX }-question__option-text` }>
                                  <div className={ `${ CLASS_PREFIX }-question__option-label` }>
                                    { option.label }
                                  </div>
                                { option.description ?
                                  <div className={ `${ CLASS_PREFIX }-question__option-description` }>
                                    { option.description }
                                  </div>
                                  :
                                  null
                                }
                                </span>
                            </button>
                          )
                        }
                      </div>
                  }
                </div>
              }
            </div>
          </div>

          {
            (currentQuestionIndex > 0 || noPreferenceOption || currentRange) &&
            <div className={ [`${ CLASS_PREFIX }__buttons`, ...fadeClasses].join(` `) }>
              {
                currentQuestionIndex > 0 &&
                <>
                  <Button onClick={ goBack } className={ `${ CLASS_PREFIX }__back` } variant={ `secondary` }>
                    <span className={ `${ CLASS_PREFIX }__back-icon` }>
                      <IconChevronLeft/>
                    </span>
                    { t(`selection.button.back`) }
                  </Button>

                  <Button className={ `${ CLASS_PREFIX }__restart` } onClick={ onRestart } variant={ `secondary` }>
                    <span className={ `${ CLASS_PREFIX }__restart-icon` }>
                      <IconReset/>
                    </span>
                    { t(`selection.button.restart`) }
                  </Button>
                </>
              }
              {
                currentQuestion &&
                <div className={ `${ CLASS_PREFIX }__buttons-end` }>
                  {
                    noPreferenceOption &&
                    <Button
                      className={ `${ CLASS_PREFIX }__no-preference` }
                      variant={ `secondary` }
                      disabled={ loading }
                      onClick={ () => selectAnswer(currentQuestion, noPreferenceOption) }
                    >
                      { noPreferenceOption.label }
                      <span className={ `${ CLASS_PREFIX }__no-preference-icon` }>
                        <IconSkip/>
                      </span>
                    </Button>
                  }
                  {
                    currentRange &&
                    <Button
                      className={ `${ CLASS_PREFIX }__confirm` }
                      variant={ `primary` }
                      disabled={ loading }
                      onClick={ () => selectAnswer(currentQuestion, toRangeOption(currentRange)) }
                    >
                      { t(`core.button.next`) }
                    </Button>
                  }
                </div>
              }
            </div>
          }
        </>
      </Dialog>
    </>
  )
}

// Ease-out: most selections end well before the question limit is
// reached, so the early questions should visibly move the bar.
const easeOut = (fraction: number): number => 1 - (1 - Math.min(1, fraction)) ** 2;

// The current question counts as half done.
const getProgress = (questionNumber: number, questionLimit: number): number => {
  const start = easeOut((questionNumber - 1) / questionLimit);
  const end = easeOut(questionNumber / questionLimit);
  return (start + end) / 2 * 100;
}

type SelectionRangeQuestionProps = {
  question: SelectionQuestion;
  value: SelectionRange;
  onChange: (range: SelectionRange) => void;
  disabled: boolean;
}

// Finer control near 0, coarser steps once the range is large enough
// that single-unit precision stops being useful.
const getStepForValue = (value: number): number => {
  const absoluteValue = Math.abs(value);
  if (absoluteValue > 1000) {
    return 100;
  }
  if (absoluteValue > 100) {
    return 10;
  }
  return 1;
}

const snapToStep = (value: number): number => {
  const step = getStepForValue(value);
  return Math.round(value / step) * step;
}

const floorToStep = (value: number): number => {
  const step = getStepForValue(value);
  return Math.floor(value / step) * step;
}

const ceilToStep = (value: number): number => {
  const step = getStepForValue(value);
  return Math.ceil(value / step) * step;
}

const getRangeBounds = (question: SelectionQuestion): SelectionRange => ({
  min: floorToStep(Number(question.options[0]?.id)),
  max: ceilToStep(Number(question.options[1]?.id)),
});

const getInitialRange = (question: SelectionQuestion): SelectionRange => {
  const answer = selectionStore.getAnswer(question);
  if (`min` in answer) {
    return { min: floorToStep(answer.min), max: ceilToStep(answer.max) };
  }
  return getRangeBounds(question);
}

const toRangeOption = (range: SelectionRange): SelectionOption => {
  const id = `${ range.min }${ RANGE_SEPARATOR }${ range.max }`;
  return { id, label: id, description: `` };
}

const SelectionRangeQuestion = ({ question, value, onChange, disabled }: SelectionRangeQuestionProps) => {
  const { min, max } = getRangeBounds(question);
  const { min: lowValue, max: highValue } = value;

  const setLowValue = (low: number) => onChange({ min: low, max: highValue });
  const setHighValue = (high: number) => onChange({ min: lowValue, max: high });

  const trackRef = useRef<HTMLDivElement>(null);

  // Keeps the handles at least one step apart, regardless of which one
  // (dragging, keyboard, or a track click) is moving.
  const clampLow = (value: number, step: number) => Math.max(min, Math.min(value, highValue - step));
  const clampHigh = (value: number, step: number) => Math.min(max, Math.max(value, lowValue + step));

  const onLowChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = snapToStep(Number(event.target.value));
    setLowValue(clampLow(value, getStepForValue(value)));
  }

  const onHighChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = snapToStep(Number(event.target.value));
    setHighValue(clampHigh(value, getStepForValue(value)));
  }

  const onTrackClick = (event: MouseEvent<HTMLDivElement>) => {
    if (disabled || !trackRef.current) {
      return;
    }

    const rect = trackRef.current.getBoundingClientRect();
    const fraction = rect.width ? (event.clientX - rect.left) / rect.width : 0;
    const value = snapToStep(min + Math.max(0, Math.min(1, fraction)) * (max - min));
    const step = getStepForValue(value);

    if (Math.abs(value - lowValue) <= Math.abs(value - highValue)) {
      setLowValue(clampLow(value, step));
    } else {
      setHighValue(clampHigh(value, step));
    }
  }

  // 0% when min === max, but that case never reaches a range slider.
  const range = max - min;
  const lowPercent = range ? ((lowValue - min) / range) * 100 : 0;
  const highPercent = range ? ((highValue - min) / range) * 100 : 100;

  const trackStyle = {
    '--ws-selection-range-low': `${ lowPercent }%`,
    '--ws-selection-range-high': `${ highPercent }%`,
  } as CSSProperties;

  const config = useConfig();

  let unit = question.unit;
  let lowDisplayValue: string = lowValue.toString();
  let highDisplayValue: string = highValue.toString();
  if (unit === 'MONEY') {
    unit = null;
    lowDisplayValue = uiHelper.formatPrice(lowValue, config.language, config.currency);
    highDisplayValue = uiHelper.formatPrice(highValue, config.language, config.currency);
  }
  const displayValue = `${ lowDisplayValue } - ${ highDisplayValue } ${ unit ? unit : `` }`;

  return (
    <div className={ `${ CLASS_PREFIX }-question__range` }>
      <div
        ref={ trackRef }
        className={ `${ CLASS_PREFIX }-question__range-track ${ CLASS_PREFIX }-question__range-track--${ disabled ? `disabled` : `enabled` }` }
        style={ trackStyle }
        onClick={ onTrackClick }
      >
        <input
          type={ `range` }
          className={ `${ CLASS_PREFIX }-question__range-input` }
          min={ min }
          max={ max }
          value={ lowValue }
          disabled={ disabled }
          onChange={ onLowChange }
        />
        <input
          type={ `range` }
          className={ `${ CLASS_PREFIX }-question__range-input` }
          min={ min }
          max={ max }
          value={ highValue }
          disabled={ disabled }
          onChange={ onHighChange }
        />
      </div>
      <div
        className={ `${ CLASS_PREFIX }-question__range-value ${ CLASS_PREFIX }-question__range-value--${ disabled ? `disabled` : `enabled` }` }>
        { displayValue }
      </div>
    </div>
  )
}

type SelectionResultProps = {
  products: Product[];
}

const SelectionResult = ({ products }: SelectionResultProps) => {
  const { t } = useTranslation();

  const trackEvent = useTrackEvent();

  useEffect(() => {
      trackEvent(
        'show',
        products.length === 0 ? 'selection_dialog_no_result' : 'selection_dialog_result',
        null,
        products,
      );
    },
    [products],
  );

  if (products.length === 0) {
    return t(`selection.no_result`);
  }

  return (
    <div className={ `${ CLASS_PREFIX }-result` }>
      {
        products.map((product, index) => {
          let label = null;
          if (index === 0) {
            label = product.score === 100 ? t('selection.winner_perfect') : t('selection.winner_best');
          }
          return (
            <SelectionProduct
              key={ product.id }
              product={ product }
              label={ label }
              collapsible={ index !== 0 }
            />
          )
        })
      }
    </div>
  )
}

export default Selection;
